import ReactECharts from 'echarts-for-react';
import selfData from '../data/stackSelfMeasurement.json';
import { useIsNarrow } from '../../hooks/useIsNarrow';

/**
 * EdgeConcentrationChart — how many of our provider endpoints terminate in the
 * same autonomous system (Fig 5).
 *
 * The vendor list is ours; the ASN grouping is measured by
 * playano/tools/measure_stack_dependence.py. Everything below is derived from
 * the committed artifact — no counts are typed in here.
 */

const BRAND = {
  orange: '#FD9800',
  muted: '#9CA3AF',
  mutedDark: '#6B7280',
  surface: '#171717',
};

type Endpoint = { host: string; asn: string | null; org: string | null; hops?: number | null };

export const EdgeConcentrationChart = ({ height = 300 }: { height?: number }) => {
  const narrow = useIsNarrow();
  const endpoints = (
    selfData as unknown as { network: { endpoints: Record<string, Endpoint> } }
  ).network.endpoints;

  const byAsn = new Map<string, { providers: string[]; org: string }>();
  for (const [provider, e] of Object.entries(endpoints)) {
    if (!e.asn) continue;
    const entry = byAsn.get(e.asn) ?? { providers: [], org: e.org ?? e.asn };
    entry.providers.push(provider);
    byAsn.set(e.asn, entry);
  }

  const rows = [...byAsn.entries()]
    .map(([asn, v]) => ({
      asn,
      org: v.org.split(',')[0].replace(/^[A-Z0-9-]+\s*-\s*/, ''),
      vendors: v.providers.sort(),
      count: v.providers.length,
    }))
    .sort((a, b) => b.count - a.count);

  const option = {
    backgroundColor: BRAND.surface,
    animation: false,
    tooltip: {
      trigger: 'item',
      backgroundColor: '#0a0a0a',
      borderColor: '#333',
      textStyle: { color: '#f5f5f5', fontSize: 13 },
      formatter: (p: { dataIndex: number }) => {
        const r = rows[p.dataIndex];
        return `<b>${r.org}</b> (${r.asn})<br/>${r.count} of ${Object.keys(endpoints).length} provider endpoints<br/><span style="color:${BRAND.muted}">${r.vendors.join(', ')}</span>`;
      },
    },
    grid: { left: narrow ? 96 : 118, right: narrow ? 30 : 44, top: 16, bottom: narrow ? 30 : 34 },
    xAxis: {
      type: 'value',
      minInterval: 1,
      axisLabel: { color: BRAND.muted, fontSize: narrow ? 11 : 12 },
      splitLine: { lineStyle: { color: '#333', opacity: 0.5 } },
    },
    yAxis: {
      type: 'category',
      data: rows.map((r) => r.org),
      inverse: true,
      axisLine: { lineStyle: { color: '#333' } },
      axisTick: { show: false },
      axisLabel: {
        color: '#e5e5e5',
        fontSize: narrow ? 11 : 13,
        fontWeight: 700,
        formatter: (v: string) => (v.length > 14 ? `${v.slice(0, 13)}…` : v),
      },
    },
    series: [
      {
        type: 'bar',
        barWidth: '52%',
        data: rows.map((r) => ({
          value: r.count,
          itemStyle: {
            color: r.count > 1 ? BRAND.orange : BRAND.mutedDark,
            borderRadius: [0, 4, 4, 0],
          },
        })),
        label: {
          show: true,
          position: 'right',
          color: '#e5e5e5',
          fontSize: narrow ? 10 : 11,
          fontWeight: 700,
          formatter: (p: { dataIndex: number }) => `${rows[p.dataIndex].count} of ${total}`,
        },
      },
    ],
  };

  const total = Object.keys(endpoints).length;
  const shared = rows.find((r) => r.count > 1);
  return (
    <div className="my-8 rounded-xl overflow-hidden border border-[#333] bg-[#171717] p-2">
      <ReactECharts option={option} style={{ height }} opts={{ renderer: 'canvas' }} />
      <p className="mt-2 text-xs text-gray-400 text-center px-4">
        Figure 5. Provider endpoints grouped by the autonomous system that terminates them, measured from this
        machine on 20 October 2026.{' '}
        {shared
          ? `${shared.org} terminates ${shared.vendors.join(', ')} — ${shared.count} of ${total}. The others each own their network.`
          : null}{' '}
        Hover a bar for the hosts. Four vendor names, one edge: the vendor count is not the independence count.
      </p>
    </div>
  );
};
